import React, { Fragment, useState } from 'react';
import { ModalFooter } from 'reactstrap';
import styled, { useTheme } from 'styled-components';
import sortBy from 'lodash/sortBy';
import Icon from '@minecraft.icon';
import { gqlProfileMedia, gqlMediaTypeCodeEnum as MEDIA_TYPE } from '@minecraft.graphql-types';
import CloudinaryVideoAudio from '@minecraft.cloudinary-video-audio';
import CloudinaryPhoto from '@minecraft.cloudinary-photo';
import { media as mediaSize, Theme } from '@minecraft.themes';
import Button from '@minecraft.button';
import { MediaHelper } from '@blocs.common';
import { WindowWidthContext } from '@minecraft.utils';
import { useTranslation } from '@blocs.i18n';
import { ReorderedMedia } from '../types';

const RearrangeMediaWrapper = styled.div<{ mediaType: string }>`
  display: grid;
  grid-template-columns: min-content;
  grid-gap: 1rem;
  max-height: calc(80vh - 6rem);
  overflow-y: auto;
  justify-content: space-around;
  ${mediaSize.md`
    grid-template-columns: ${(props): string =>
      props.mediaType !== MEDIA_TYPE.PHOTO ? '9rem 9rem 9rem' : '11.3rem 11.3rem 11.3rem'};
  `}
  ${mediaSize.lg`
     grid-template-columns: ${(props): string =>
       props.mediaType !== MEDIA_TYPE.PHOTO ? '9rem 9rem 9rem 9rem 9rem' : '11.3rem 11.3rem 11.3rem 11.3rem'};
  `}
`;

const StyledMedia = styled.div`
  display: grid;
  grid-template-rows: 1fr min-content;
  position: relative;
  margin-top: 1.5rem;
`;

const StyledModalFooter = styled(ModalFooter)`
  border-top: none;
  display: flex;
  flex-flow: column-reverse;
  ${mediaSize.sm`
    flex-flow: row wrap;
  `}
`;

const StyledSubmitButton = styled(Button)`
  width: 100%;
  ${mediaSize.sm`
    width: auto;
  `}
`;

export const StyledCancelButton = styled(Button)`
  text-decoration: none;
  color: ${(props): string => props.theme.colors.primary.grey2};
  &:hover {
    color: ${(props): string => props.theme.colors.primary.grey1};
    text-decoration: none;
  }
  width: 100%;
  border: 2px solid;
  ${mediaSize.sm`
    width: auto;
    border: none;
  `}
`;

const MediaSelectionContainer = styled.div`
  display: flex;
  padding: 0.5rem 0 0;
  margin: auto;
  svg {
    width: 3rem;
    height: 3rem;
  }
  ${mediaSize.sm`
    svg {
      width: 2rem;
      height: 2rem;
    }
  `}
`;

const ReorderButtonWrapper = styled.div`
  display: flex;
  align-self: center;
`;

const DisabledReorderButton = styled(Icon)`
  cursor: default;
  width: 2rem;
  height: 2rem;
`;

const EnabledReorderButton = styled(Icon)`
  cursor: pointer;
  width: 2rem;
  height: 2rem;
`;

const MediaName = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${mediaSize.md` font-size: 0.8rem; `}
`;

const PrimaryLabel = styled.div`
  color: ${(props): string => props.theme.colors.primary.white};
  background: ${(props): string => props.theme.colors.primary.grey1};
  position: absolute;
  top: -1.5rem;
  font-size: 0.875rem;
  width: 91%;
  right: 0;
  text-align: center;
  line-height: 1.5rem;
`;

const StyledMediaContainer = styled.div`
  display: grid;
  align-content: center;
  justify-content: center;
  border: 1px solid ${(props): string => props.theme.colors.primary.ltGrey1};
`;

const StyledVideoAudioContainer = styled(StyledMediaContainer)`
  border: none;
`;

const UserInfoText = styled.div`
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
`;

const getMediaComponent = (qaId, media, widthData): JSX.Element => {
  switch (media.mediaType.code) {
    case MEDIA_TYPE.AUDIO:
    case MEDIA_TYPE.VIDEO:
      return (
        <StyledVideoAudioContainer>
          <CloudinaryVideoAudio
            controls
            guid={media.guid}
            fileKey={media.fileKey}
            mediaStorageStatus={media.mediaStorageStatus.code}
            thumbnailUrl={media.thumbnailUrl}
            thumbnail={media.thumbnail}
            mediaType={media.mediaType.code}
            type={widthData.lessSm ? 'sm' : 'xsm'}
            isThumbnail
            mediaStatus={media.mediaStatus.code}
            data-qa-id={`${qaId}-${media.mediaType.code.toLowerCase()}`}
          />
          <MediaName>{media.name}</MediaName>
        </StyledVideoAudioContainer>
      );
    case MEDIA_TYPE.PHOTO:
      return (
        <StyledMediaContainer>
          <CloudinaryPhoto
            data-qa-id={`${qaId}`}
            guid={media.guid}
            fileKey={media.fileKey}
            type="sm"
            transformation={media.transformation}
            mediaStorageStatus={media.mediaStorageStatus}
          />
        </StyledMediaContainer>
      );
    default:
      return null;
  }
};

export interface RearrangeMediaProps {
  'data-qa-id'?: string;
  mediaAssets: gqlProfileMedia[];
  handleToggleClick: () => void;
  handleSubmitClick: (mediaAssets: ReorderedMedia[]) => void;
  mediaType: string;
}

const RearrangeMedia = (props: RearrangeMediaProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme() as Theme;
  const { 'data-qa-id': qaId, mediaAssets, handleToggleClick, handleSubmitClick, mediaType } = props;
  const [sortedMediaAssets, setSortedMediaAssets] = useState(
    sortBy(mediaAssets, [(asset) => mediaType === MEDIA_TYPE.PHOTO && !asset.isPrimary, (asset) => asset.order]).map(
      (item) => ({ ...item })
    )
  );
  const { useWidthData } = WindowWidthContext;
  const widthData = useWidthData();

  const updateIsPrimary = (sortedAssets): void => {
    const newSortedAssets = [...sortedAssets];

    if (mediaType === MEDIA_TYPE.PHOTO) {
      newSortedAssets.forEach((media, index): void => {
        newSortedAssets[index].isPrimary = index === 0;
      });
    }

    setSortedMediaAssets(newSortedAssets);
  };

  const handleSorting = (index, isUp, skipAll): void => {
    const sortedMedia = MediaHelper.rearrangeMedias(sortedMediaAssets, index, isUp, skipAll);

    updateIsPrimary(sortedMedia);
  };

  const saveSorting = (): void => {
    handleSubmitClick(
      sortedMediaAssets.map(({ profileMediaId, hidden, isPrimary }, index) => ({
        profileMediaId,
        isHidden: hidden,
        isPrimary,
        order: index + 1,
      }))
    );
  };

  const getUserInfoText = () => {
    if (mediaType === MEDIA_TYPE.PHOTO) {
      return t('common:mediaType.primaryPhotoRequirement');
    }

    const mediaText = t(`common:mediaType.${mediaType.toLowerCase()}`);

    return t('common:mediaType.reelOrder', { mediaType: mediaText });
  };

  const getPrimaryText = () => {
    return t(`common:mediaType.${mediaType.toLowerCase()}Item`);
  };

  return (
    <>
      <UserInfoText>{getUserInfoText()}</UserInfoText>
      <RearrangeMediaWrapper mediaType={mediaType}>
        {sortedMediaAssets.map((mediaInfo, index) => (
          <Fragment key={mediaInfo.media.mediaId}>
            <StyledMedia>
              {mediaInfo.isPrimary && <PrimaryLabel>{getPrimaryText()}</PrimaryLabel>}
              {getMediaComponent(qaId, mediaInfo.media, widthData)}
              <MediaSelectionContainer>
                <ReorderButtonWrapper>
                  {index ? (
                    <EnabledReorderButton
                      data-qa-id={`${qaId}-first-place-default-icon`}
                      name="first-place-default"
                      color={theme.colors.primary.primary}
                      onClick={(): void => handleSorting(index, true, true)}
                    />
                  ) : (
                    <DisabledReorderButton
                      data-qa-id={`${qaId}-first-place-disabled-icon`}
                      name="first-place-disabled"
                      color={theme.colors.primary.ltGrey1}
                    />
                  )}
                  {index ? (
                    <EnabledReorderButton
                      data-qa-id={`${qaId}-boxed-chevron-closed-icon`}
                      name="boxed-chevron-closed"
                      color={theme.colors.primary.primary}
                      onClick={(): void => handleSorting(index, true, false)}
                    />
                  ) : (
                    <DisabledReorderButton
                      data-qa-id={`${qaId}-boxed-chevron-closed-disabled-icon`}
                      name="boxed-chevron-closed"
                      color={theme.colors.primary.ltGrey1}
                    />
                  )}
                  {index !== sortedMediaAssets.length - 1 ? (
                    <EnabledReorderButton
                      data-qa-id={`${qaId}-boxed-chevron-open-icon`}
                      name="boxed-chevron-open"
                      color={theme.colors.primary.primary}
                      onClick={(): void => handleSorting(index, false, false)}
                    />
                  ) : (
                    <DisabledReorderButton
                      data-qa-id={`${qaId}-boxed-chevron-open-disabled-icon`}
                      name="boxed-chevron-open"
                      color={theme.colors.primary.ltGrey1}
                    />
                  )}

                  {index !== sortedMediaAssets.length - 1 ? (
                    <EnabledReorderButton
                      data-qa-id={`${qaId}-last-place-default-icon`}
                      name="last-place-default"
                      color={theme.colors.primary.primary}
                      onClick={(): void => handleSorting(index, false, true)}
                    />
                  ) : (
                    <DisabledReorderButton
                      data-qa-id={`${qaId}-last-place-disabled-icon`}
                      name="last-place-disabled"
                      color={theme.colors.primary.ltGrey1}
                    />
                  )}
                </ReorderButtonWrapper>
              </MediaSelectionContainer>
            </StyledMedia>
          </Fragment>
        ))}
      </RearrangeMediaWrapper>

      <StyledModalFooter>
        <StyledCancelButton data-qa-id={`${qaId}-cancel-button`} onClick={handleToggleClick} color="link">
          {t('common:button.cancel')}
        </StyledCancelButton>
        <StyledSubmitButton
          data-qa-id={`${qaId}-save-order-button`}
          color="primary"
          size="small"
          onClick={(): void => saveSorting()}
        >
          {t('common:button.saveOrder')}
        </StyledSubmitButton>
      </StyledModalFooter>
    </>
  );
};

export default RearrangeMedia;
